import ReactMarkdown from 'react-markdown'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import CancelIcon from '@mui/icons-material/Cancel'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import PetsIcon from '@mui/icons-material/Pets'
import PetsOutlinedIcon from '@mui/icons-material/PetsOutlined'
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, IconButton, Modal, Paper, Typography } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { translateText } from '../../../language'
import { setAccorditionExpanded } from '../../../state/events'
import { toggleFavoriteWithSync } from '../../../state/events/favoritesThunks'
import { getConventionYear } from '../../../state/events/favoritesStorage'
import { sizing, useThemeMode } from '../../../theme'
import { isDefined, notDefined } from '../../../utils'
import { Button, Loading, NoData, TranslatableTypography } from '../widgets'

const getThemeSx = (palette) => ({
  '&.companion-event-browser-wrapper': {
    padding: '5px',
    '& .Mui-expanded': {
      margin: '0px'
    },
    '& .MuiPaper-root.MuiAccordion-rounded': {
      background: palette.headerColor
    }
  },
  '& .companion-event-browser-content': {
    borderRadius: '5px',
    background: palette.paperColor,
    position: 'relative'
  },
  '& .companion-event-browser-day': {
    position: 'relative'
  },
  '& .MuiAccordionDetails-root.companion-event-browser-day': {
    padding: '0px'
  },
  '& .companion-event-browser-day-scroll-pivot': {
    position: 'absolute',
    top: `-${sizing.header.height}px`
  },
  '& .MuiAccordionSummary-content .MuiTypography-root': {
    display: 'inline-block',
    marginRight: '10px'
  },
  '& .MuiAccordionSummary-content .MuiTypography-root:last-of-type': {
    fontSize: '12px'
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: palette.paperColor
  },
  '& .companion-event-browser-day-header': {
    background: palette.headerColor,
    borderRadius: '5px 5px 0px 0px',
    padding: '10px',
    fontWeight: 600,
    width: '100%'
  },
  '& .MuiAccordion-root': {
    borderBottom: `2px solid ${palette.paperColor}`
  },
  '& .MuiAccordion-root:last-of-type': {
    borderBottom: '0px'
  },
  '& .MuiAccordionSummary-root.companion-event-browser-day-header': {
    height: '45px',
    maxHeight: '45px',
    minHeight: '45px',
    padding: '0px 10px 0px 10px'
  },
  '& .companion-event-browser-event-time': {
    '& .MuiBox-root': {
      textAlign: 'center',
      '& .MuiTypography-root': {
        display: 'inline',
        margin: '5px',
        fontWeight: '600',
        color: palette.headerColor
      }
    }
  },
  '& .companion-event-browser-event-icons': {
    padding: '5px',
    textAlign: 'center',
    '& .MuiSvgIcon-root': {
      width: '20px',
      height: '20px',
      color: palette.headerColor
    },
    '& .MuiSvgIcon-root:first-of-type': {
      marginRight: '5px'
    }
  },
  '& .finished .companion-event-browser-event-icons .MuiSvgIcon-root': {
    color: 'rgba(0, 0, 0, 0.25)'
  },
  '& .companion-event-browser-event': {
    padding: '5px',
    paddingLeft: '0px',
    position: 'relative',
    borderLeft: `10px solid ${palette.headerTransparentThinColor}`,
    '&.finished': {
      color: 'rgba(0, 0, 0, 0.25)',
      borderLeft: `10px solid ${palette.transparentAlfa}`
    },
    '&.in-progress': {
      borderLeft: `10px solid ${palette.successText}`
    },
    '& .companion-event-browser-modal-opener': {
      display: 'block',
      position: 'absolute',
      left: 0,
      top: 0,
      width: '100%',
      height: '100%'
    },
    '& .MuiGrid-root': {
      padding: '5px'
    }
  },
  '& .companion-event-browser-event:nth-of-type(even)': {
    background: palette.paperColorDim
  },
  '& .companion-event-browser-event:nth-of-type(odd)': {
    background: palette.paperColorDim2
  },
  '& .companion-event-browser-event-header': {
    '& .MuiTypography-root': {
      display: 'inline',
      margin: '5px',
      fontWeight: '600',
      color: palette.header
    }
  },
  eventModal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .companion-event-browser-modal-content': {
      minWidth: '60%',
      maxWidth: '80%',
      position: 'relative',
      '& .modal-content-title': {
        background: palette.headerColor,
        padding: '10px',
        fontWeight: 600,
        color: 'white'
      },
      '& .companion-event-browser-modal-close-button': {
        position: 'absolute',
        width: '20px',
        height: '20px',
        right: '10px',
        top: '10px',
        color: palette.paperColor,
        background: palette.headerColor,
        '&:hover': {
          background: palette.headerBrightColor
        }
      },
      '& .modal-content-sub-title': {
        paddingLeft: '10px',
        paddingRight: '10px',
        background: palette.paperColorDim2,
        fontWeight: 600,
        color: palette.headerColor,
        '& .MuiTypography-root': {
          display: 'inline',
          margin: '5px',
          fontWeight: '600',
          color: palette.headerColor
        }
      },
      '& .modal-content-content': {
        padding: '10px',
        textAlign: 'left',
        maxHeight: '300px',
        overflowY: 'scroll',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: palette.headerColor,
          borderRadius: '3px',
          opacity: 0.6
        },
        '& .modal-content-label': {
          marginTop: '10px',
          color: palette.headerColor
        }
      },
      '& .modal-content-controls': {
        marginTop: '10px',
        padding: '10px',
        textAlign: 'right'
      }
    }
  }
})

const ConopsDetail = (props) => {
  const { assigned, note, equipment } = props
  const conopsMembersLabel = assigned?.length > 0 ? <TranslatableTypography className='modal-content-label'>conops-assigned</TranslatableTypography> : undefined
  const conopsMembers = assigned?.length > 0 ? <Typography>{assigned.map(a => a?.nick).join(', ')}</Typography> : undefined
  const noteLabelElement = isDefined(note) ? <TranslatableTypography className='modal-content-label'>conops-note</TranslatableTypography> : undefined
  const noteElement = isDefined(note) ? <ReactMarkdown>{note}</ReactMarkdown> : undefined
  const equipmentLabelElement = isDefined(equipment) ? <TranslatableTypography className='modal-content-label'>equipment</TranslatableTypography> : undefined
  const equipmentElement = isDefined(equipment) ? <ReactMarkdown>{equipment}</ReactMarkdown> : undefined

  return (
    <Grid item xs={12} className='modal-content-conops'>
      {conopsMembersLabel}
      {conopsMembers}
      {noteLabelElement}
      {noteElement}
      {equipmentLabelElement}
      {equipmentElement}
    </Grid>
  )
}

const SecurityDetail = (props) => {
  const { assigned, note } = props
  const securityMembersLabel = assigned?.length > 0 ? <TranslatableTypography className='modal-content-label'>security-assigned</TranslatableTypography> : undefined
  const securityMembers = assigned?.length > 0 ? <Typography>{assigned.map(a => a?.nick).join(', ')}</Typography> : undefined
  const noteLabelElement = isDefined(note) ? <TranslatableTypography className='modal-content-label'>security-note</TranslatableTypography> : undefined
  const noteElement = isDefined(note) ? <ReactMarkdown>{note}</ReactMarkdown> : undefined
  return (
    <Grid item xs={12} className='modal-content-security'>
      {securityMembersLabel}
      {securityMembers}
      {noteLabelElement}
      {noteElement}
    </Grid>
  )
}

const PresentersDetail = (props) => {
  const { presenters } = props
  const presentersLabel = presenters?.length > 0 ? <TranslatableTypography className='modal-content-label'>presenters</TranslatableTypography> : undefined
  const presentersValue = presenters?.length > 0 ? <Typography>{presenters.map(a => a?.nick).join(', ')}</Typography> : undefined
  return (
    <Grid item xs={12} className='modal-content-security'>
      {presentersLabel}
      {presentersValue}
    </Grid>
  )
}

export const EventDetail = (props) => {
  const { id, day, start, end, name, description, favorite, presenters, conops, security, location } = props
  const dispatch = useDispatch()
  const config = useSelector((state) => state?.config?.config)
  const auth = useSelector((state) => state?.auth)
  const year = getConventionYear(config)
  const setFavoriteDispatcher = (fav) => dispatch(toggleFavoriteWithSync(config, auth, id, fav, year))

  const descriptionGrid = isDefined(description) ? (<Grid item xs={12} className='modal-content-describtion'><ReactMarkdown>{description}</ReactMarkdown></Grid>) : undefined
  const presentersGrid = isDefined(presenters) ? (<PresentersDetail presenters={presenters} />) : undefined
  const conopsGrid = isDefined(conops) ? (<ConopsDetail {...conops} />) : undefined
  const securityGrid = isDefined(security) ? (<SecurityDetail {...security} />) : undefined

  let favoriteButton
  if (favorite) {
    favoriteButton = (
      <Button variant="contained" startIcon={<PetsIcon />} onClick={() => setFavoriteDispatcher(false)} >
        remove favorite
      </Button>
    )
  } else {
    favoriteButton = (
      <Button variant="contained" startIcon={<PetsIcon />} onClick={() => setFavoriteDispatcher(true)} >
        add favorite
      </Button>
    )
  }

  return (
    <Paper>
      <Grid container>
        <Grid item xs={12} className='modal-content-title'>{name}</Grid>
        <Grid item xs={12} className='modal-content-sub-title' style={{ paddingTop: '10px' }}>
          <Typography>{day}</Typography>
        </Grid>
        <Grid item xs={12} className='modal-content-sub-title'>
          <Typography fontSize='small'>{start}</Typography>
          <Typography fontSize='small'>-</Typography>
          <Typography fontSize='small'>{end}</Typography>
        </Grid>
        <Grid item xs={12} className='modal-content-sub-title' style={{ paddingBottom: '10px' }}>
          <LocationOnIcon /><Typography>{location}</Typography>
        </Grid>
        <Grid item xs={12} className='modal-content-content'>
          <Grid container >
            {descriptionGrid}
            {presentersGrid}
            {conopsGrid}
            {securityGrid}
          </Grid>
        </Grid>
        <Grid item xs={12} className='modal-content-controls'>
          {favoriteButton}
        </Grid>
      </Grid>
    </Paper>
  )
}

const Event = (props) => {
  const [open, setOpen] = useState(false)
  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const { palette } = useThemeMode()
  const themeSX = getThemeSx(palette)
  const { event, locale, day } = props
  const { id, start, end, name, descriptions, favorite, conops, security, location, presenters } = event
  const timeFormatOptions = { hour: '2-digit', minute: '2-digit' }
  const translation = useTranslation()
  const separator = <Typography fontSize='small'>-</Typography>
  const startDate = new Date(start)
  const icons = []
  if (isDefined(conops) || isDefined(security)) {
    icons.push(<AssignmentOutlinedIcon key='conops-security' />)
  }
  if (isDefined(favorite)) {
    icons.push(<PetsOutlinedIcon key='favorite' />)
  }

  const startTime = startDate.toLocaleTimeString(locale, timeFormatOptions)
  const startTimeTypography = (
    <Typography fontSize='small'>
      {startTime}
    </Typography>
  )

  const endDate = new Date(end)
  const endTime = endDate.toLocaleTimeString(locale, timeFormatOptions)
  const endTimeTypography = (
    <Typography fontSize='small'>
      {endTime}
    </Typography>
  )
  const tName = translateText(name, translation.i18n)
  const tDescription = translateText(descriptions, translation.i18n)
  const tLocation = translateText(location?.name, translation.i18n)
  const now = new Date()

  let progressClass = ''
  if (startDate <= now && endDate <= now) {
    progressClass = 'finished'
  } else if (startDate <= now && endDate >= now) {
    progressClass = 'in-progress'
  }
  return (
    <Grid container className={`companion-event-browser-event ${progressClass}`}>
      <Modal open={open} onClose={handleClose} sx={themeSX.eventModal}>
        <Box className='companion-event-browser-modal-content'>
          <IconButton onClick={handleClose} className='companion-event-browser-modal-close-button'>
            <CancelIcon />
          </IconButton>
          <EventDetail
            id={id}
            start={startTime}
            end={endTime}
            day={day}
            name={tName}
            description={tDescription}
            favorite={favorite}
            conops={conops}
            security={security}
            location={tLocation}
            presenters={presenters} />
        </Box>
      </Modal>
      <Box className='companion-event-browser-modal-opener' onClick={handleOpen}/>
      <Grid item xs={4} key='event-time' className='companion-event-browser-event-time'>
        <Box>
          {startTimeTypography}
          {separator}
          {endTimeTypography}
        </Box>
        </Grid>
        <Grid item xs={6} key='event-label' className='companion-event-browser-event-header'>
          {tName}
        </Grid>
        <Grid item xs={2} key='event-icons' className='companion-event-browser-event-icons'>
          {icons}
        </Grid>
    </Grid>
  )
}

const EventsDay = (props) => {
  const { day, events } = props
  const { palette } = useThemeMode()

  const translation = useTranslation()
  const locale = translation.t('date_locale')
  const convertedDate = new Date(day)
  const localizedDate = convertedDate.toLocaleDateString(locale)
  const dayLabel = translation.t('day_' + convertedDate.getDay()) + ' - ' + localizedDate

  const header = (
    <Typography color={palette.paperColor}>
      {dayLabel}
    </Typography>
  )

  const rows = events.map((event) => (<Event key={event.id} event={event} locale={locale} day={dayLabel} />))

  return (
    <Grid item xs={12} className='companion-event-browser-day'>
      <Box className={'companion-event-browser-day-scroll-pivot ' + day} />
      <Box className='companion-event-browser-day-header'>{header}</Box>
      <Grid container className='companion-event-browser-day-events'>{rows}</Grid>
    </Grid>
  )
}

const AccorditionEventsDay = (props) => {
  const { day, events } = props
  const { palette } = useThemeMode()

  const translation = useTranslation()
  const dispatch = useDispatch()
  const locale = translation.t('date_locale')
  const convertedDate = new Date(day)
  const localizedDate = convertedDate.toLocaleDateString(locale)
  const localizedDay = translation.t('day_' + convertedDate.getDay())
  const dayLabel = `${localizedDay} - ${localizedDate}`

  const { expanded } = useSelector(state => state?.schedule?.accordition)
  const header = (
    <Box>
      <Typography color={palette.paperColor}>
        {localizedDay}
      </Typography>
      <Typography color={palette.paperColor}>
        {localizedDate}
      </Typography>
    </Box>
  )

  const accorditionChange = (day) => (event, isExpanded) => {
    if (isExpanded && expanded !== day) {
      dispatch(setAccorditionExpanded({ expanded: day }))
    }
  }

  const rows = events.map((event) => (<Event key={event.id} event={event} locale={locale} day={dayLabel} />))
  return (
    <Accordion expanded={expanded === day} onChange={accorditionChange(day)}>
      <AccordionSummary className='companion-event-browser-day-header' expandIcon={<ExpandMoreIcon />}>
        {header}
      </AccordionSummary>
      <AccordionDetails className='companion-event-browser-day'>
        {rows}
      </AccordionDetails>
    </Accordion>
  )
}

export const Browser = (props) => {
  const { data, noData } = props
  const { palette } = useThemeMode()
  const navigation = useSelector((state) => state?.navigation)
  const eventView = useSelector((state) => state?.eventsMenu?.view)
  const { loading } = navigation

  if (loading && notDefined(data)) {
    return <Loading />
  }
  const themeSX = getThemeSx(palette)

  const days = []
  data.forEach(entry => {
    const day = entry[0]
    const events = entry[1]
    const eventProps = {
      key: day,
      day,
      events
    }
    if (events?.length > 0) {
      if (eventView === 'day') {
        days.push(<AccorditionEventsDay {...eventProps} />)
      } else {
        days.push(<EventsDay {...eventProps} />)
      }
    }
  })

  if (notDefined(data) || days.length === 0) {
    return <NoData {...noData}/>
  }

  if (eventView === 'day') {
    return (
      <Box className='companion-event-browser-wrapper' sx={themeSX}>
        {days}
      </Box>
    )
  } else {
    return (
      <Box className='companion-event-browser-wrapper' sx={themeSX}>
        <Grid container className='companion-event-browser-content' >
          {days}
        </Grid>
      </Box>
    )
  }
}
